import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Download, Loader2 } from "lucide-react";
import api, { apiErrorMessage } from "../../lib/api";
import { downloadBlob } from "../../lib/downloadBlob";
import { asciiName, versionLine } from "../../lib/portalPresence";
import { longDate, whenLabel } from "../../lib/portalPortfolio";
import { ROUND_LABELS, SHEET_STATES, conditionLabel, minutesLabel } from "../../lib/portalComposition";
import { Chip } from "./PortalShell";
import DatePickerField from "../admin/DatePickerField";

const card = "rounded-2xl border border-[#E5E7EB] bg-white";
const quiet = "inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] bg-white px-3 py-1.5 text-xs font-semibold text-[#373A45] hover:border-[#965EA7] hover:text-[#965EA7] disabled:opacity-50";
const field = "w-full rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 text-sm text-[#373A45] focus:border-[#965EA7] focus:outline-none focus:ring-2 focus:ring-[#965EA7]/20";

/**
 * The team's view of the final evaluation committee and the composition stage
 * (portal PR 10): the committee, each evaluator's sheet, the means once every
 * sheet is signed, the three conditions for the final evaluation, Anexele 8
 * and 9. Reopening a signed sheet needs a reason, kept in its history.
 *
 * With `round="final"` (PR 11a) the same board shows the second round, the
 * final evaluation's: Anexele 11 and 12, inside the final evaluation's board,
 * which chooses the session (`session`) and shows the committee once.
 */
export default function CompositionBoard({ groupId, round = "composition", session }) {
  const [own, setKind] = useState("main");
  const kind = session || own;
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const embedded = round !== "composition";

  const load = useCallback(async () => {
    try {
      setData((await api.get(`/admin/portal/groups/${groupId}/composition`, { params: { session: kind, round } })).data);
      setError("");
    } catch (err) {
      setError(apiErrorMessage(err, "Nu am putut încărca evaluarea."));
    }
  }, [groupId, kind, round]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="grid gap-8" data-testid={`composition-board-${round}`}>
      {!embedded && <CommitteeEditor groupId={groupId} onSaved={load} />}
      {error && <p className="text-sm text-[#c0483f]" role="alert">{error}</p>}
      {!data && !error && <Loader2 className="mx-auto animate-spin text-[#6B7280]" />}
      {data && (
        <>
          {data.has_resit && !session && (
            <div className="inline-flex w-fit overflow-hidden rounded-full border border-[#E5E7EB] bg-white" role="tablist">
              {[["main", "Sesiunea principală"], ["resit", "Sesiunea de restanță"]].map(([k, label]) => (
                <button key={k} type="button" role="tab" aria-selected={kind === k} onClick={() => setKind(k)}
                  className={`px-4 py-1.5 text-sm font-semibold ${kind === k ? "bg-[#373A45] text-white" : "text-[#373A45] hover:bg-[#F5F6F6]"}`}>
                  {label}
                </button>
              ))}
            </div>
          )}
          <Sheets data={data} groupId={groupId} kind={kind} round={round} onChanged={setData} />
          <Means data={data} />
          {!embedded && <Conditions data={data} />}
          <Annexes data={data} groupId={groupId} kind={kind} round={round} onChanged={setData} />
        </>
      )}
    </div>
  );
}

function CommitteeEditor({ groupId, onSaved }) {
  const [c, setC] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const apply = (x) => {
    setC(x);
    const m = x.committee;
    setForm({
      president: { name: m?.president?.name || "", trainer_id: m?.president?.trainer_id || "" },
      secretary: { name: m?.secretary?.name || "" },
      evaluators: (m?.evaluators || []).map((e) => e.trainer_id),
      decision_no: m?.decision_no || "", decision_date: m?.decision_date || "",
    });
  };
  useEffect(() => {
    api.get(`/admin/portal/groups/${groupId}/committee`).then((r) => apply(r.data))
      .catch((err) => toast.error(apiErrorMessage(err, "Nu am putut încărca comisia.")));
  }, [groupId]);

  if (!c || !form) return null;
  const toggle = (id) => setForm({
    ...form, evaluators: form.evaluators.includes(id) ? form.evaluators.filter((x) => x !== id) : [...form.evaluators, id],
  });
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form, president: { ...form.president, trainer_id: form.president.trainer_id || null } };
      apply((await api.put(`/admin/portal/groups/${groupId}/committee`, body)).data);
      toast.success("Comisia e salvată.");
      onSaved();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut salva comisia."));
    } finally {
      setBusy(false);
    }
  };
  const taught = form.evaluators.includes(c.group.trainer_id);

  return (
    <section aria-labelledby="cm-title">
      <h2 id="cm-title" className="font-display text-xl font-bold text-[#373A45]">Comisia de evaluare finală</h2>
      <p className="mt-1 text-sm text-[#6B7280]">
        Un președinte, un secretar și cel puțin doi membri evaluatori, formatori ai programului; cel puțin unul a predat la
        grupă (strategia, cap. II.1). Evaluatorii intră în portal cu adresa cu care predau și văd doar portofoliile admise la
        structură.
      </p>
      <div className={`mt-3 grid gap-4 p-4 ${card}`} data-testid="committee-editor">
        <fieldset>
          <legend className="text-sm font-semibold text-[#373A45]">Membrii evaluatori</legend>
          <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
            {c.trainers.map((t) => {
              const scored = c.scored.includes(t.id);
              return (
                <li key={t.id}>
                  <label className="flex items-center gap-2 text-sm text-[#373A45]">
                    <input type="checkbox" className="h-4 w-4 accent-[#965EA7]" checked={form.evaluators.includes(t.id)}
                      disabled={scored && form.evaluators.includes(t.id)} onChange={() => toggle(t.id)}
                      data-testid={`cm-ev-${t.id}`} />
                    {t.name}
                    {t.id === c.group.trainer_id && <span className="text-xs text-[#6B7280]">· a predat la grupă</span>}
                    {scored && <span className="text-xs text-[#6B7280]">· a punctat</span>}
                  </label>
                </li>
              );
            })}
          </ul>
          {form.evaluators.length < c.min_evaluators && <p className="mt-2 text-xs text-[#8a5d12]">Alege cel puțin doi evaluatori.</p>}
          {form.evaluators.length >= c.min_evaluators && !taught && (
            <p className="mt-2 text-xs text-[#8a5d12]">Cel puțin un evaluator trebuie să fi predat la grupa aceasta.</p>
          )}
        </fieldset>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm text-[#373A45]">
            Președinte
            <select className={`${field} mt-1`} value={form.president.trainer_id}
              onChange={(e) => setForm({ ...form, president: { trainer_id: e.target.value, name: e.target.value ? "" : form.president.name } })}>
              <option value="">Altcineva (scriu numele)</option>
              {c.trainers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
          {!form.president.trainer_id && (
            <label className="text-sm text-[#373A45]">
              Numele președintelui
              <input className={`${field} mt-1`} value={form.president.name}
                onChange={(e) => setForm({ ...form, president: { ...form.president, name: e.target.value } })} />
            </label>
          )}
          <label className="text-sm text-[#373A45]">
            Secretar <span className="text-xs text-[#6B7280]">(din echipă; doar nume pe anexe)</span>
            <input className={`${field} mt-1`} value={form.secretary.name}
              onChange={(e) => setForm({ ...form, secretary: { name: e.target.value } })} data-testid="cm-secretary" />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-sm text-[#373A45]">
              Decizia nr.
              <input className={`${field} mt-1`} value={form.decision_no} onChange={(e) => setForm({ ...form, decision_no: e.target.value })} />
            </label>
            <label className="text-sm text-[#373A45]">
              din
              {/* The platform's date picker (19.10.2026), like the other dates of the portal. */}
              <div className="mt-1"><DatePickerField size="sm" value={form.decision_date || ""} ariaLabel="Data deciziei"
                testid="cm-decision-date" onChange={(v) => setForm({ ...form, decision_date: v })} /></div>
            </label>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" className={quiet} disabled={busy || form.evaluators.length < c.min_evaluators} onClick={save}
            data-testid="cm-save">
            {busy && <Loader2 size={13} className="animate-spin" />} Salvează comisia
          </button>
          {c.committee && <span className="text-xs text-[#6B7280]">Salvată de {c.committee.updated_by?.name}, {whenLabel(c.committee.updated_at)}.</span>}
        </div>
      </div>
    </section>
  );
}

function Sheets({ data, groupId, kind, round, onChanged }) {
  const s = data.session;
  const L = ROUND_LABELS[round];
  return (
    <section aria-labelledby={`cp-sheets-${round}`}>
      <h2 id={`cp-sheets-${round}`} className="font-display text-xl font-bold text-[#373A45]">
        Fișele evaluatorilor{round === "final" ? " la susținere" : ""}
      </h2>
      <p className="mt-1 text-sm text-[#6B7280]">
        {s && round === "composition" ? `Termen: până pe ${longDate(s.composition_due)} (procedura, art. 28 alin. 1). ` : ""}
        Punctajele unei fișe se văd aici după ce evaluatorul o semnează.
      </p>
      {!data.basis && <p className="mt-2 text-sm text-[#8a5d12]">{L.closed}</p>}
      <ul className="mt-3 grid gap-2">
        {data.sheets.map((sh) => (
          <SheetRow key={sh.trainer_id} sh={sh} groupId={groupId} kind={kind} round={round} onChanged={onChanged}
            minMinutes={data.min_minutes}
            annex8={data.documents.find((d) => d.kind === `annex_${L.individual}` && d.latest && d.scope === `${kind}:${sh.trainer_id}`)} />
        ))}
        {data.sheets.length === 0 && <li className="text-sm text-[#6B7280]">Comisia nu e compusă.</li>}
      </ul>
    </section>
  );
}

function SheetRow({ sh, groupId, kind, round, onChanged, minMinutes, annex8 }) {
  const n = ROUND_LABELS[round].individual;
  const [reason, setReason] = useState("");
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const st = SHEET_STATES[sh.status] || SHEET_STATES.draft;
  const reopen = async () => {
    setBusy(true);
    try {
      onChanged((await api.post(`/admin/portal/groups/${groupId}/composition/${sh.trainer_id}/reopen`, { session: kind, reason },
        { params: { round } })).data);
      toast.success(`Fișa lui ${sh.name} e redeschisă.`);
      setAsking(false);
      setReason("");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut redeschide fișa."));
    } finally {
      setBusy(false);
    }
  };
  const makeAnnex8 = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/portal/groups/${groupId}/composition-documents`, {
        session: kind, kind: `annex_${n}`, evaluator_id: sh.trainer_id });
      toast.success(`Anexa ${n} a lui ${sh.name}, versiunea ${r.data.document.version}, e generată.`);
      onChanged(r.data);
    } catch (err) {
      toast.error(apiErrorMessage(err, `Nu am putut genera Anexa ${n}.`));
    } finally {
      setBusy(false);
    }
  };
  const sig = sh.signature;
  return (
    <li className={`p-4 ${card}`} data-testid={`cp-sheet-${sh.trainer_id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-[#373A45]">{sh.name}</span>
        <Chip tone={st.tone}>{st.label}</Chip>
        <span className="text-xs text-[#6B7280]">{sh.scored} din {sh.of} punctate</span>
        {sh.status === "signed" && (
          <span className="ml-auto flex flex-wrap gap-2">
            {(!annex8 || annex8.stale) && (
              <button type="button" className={quiet} disabled={busy} onClick={makeAnnex8}>Generează Anexa {n}</button>
            )}
            <button type="button" className={quiet} onClick={() => setAsking((v) => !v)}>Redeschide</button>
          </span>
        )}
      </div>
      {sig && (
        <p className="mt-1 text-xs text-[#6B7280]">
          Semnată de {sig.full_name} la {whenLabel(sig.signed_at)} · cod {sig.code}
          {sig.ip && ` · IP ${sig.ip} · ${sig.user_agent || "browser necunoscut"}`}
        </p>
      )}
      {sh.outdated && <p className="mt-1 text-xs font-semibold text-[#8a5d12]">
        Semnată pe o versiune mai veche a Anexei {round === "composition" ? "6" : "10"}: trebuie redeschisă.
      </p>}
      {sh.fast.length > 0 && (
        <p className="mt-1 flex gap-1.5 text-xs text-[#8a5d12]">
          <AlertTriangle size={13} className="mt-px shrink-0" />
          Sub {minMinutes} minute între deschidere și punctaj: {sh.fast.join(", ")}.
        </p>
      )}
      {sh.reopenings.map((r) => (
        <p key={r.at} className="mt-1 text-xs text-[#6B7280]">Redeschisă de {r.by?.name} la {whenLabel(r.at)}: {r.reason}</p>
      ))}
      {asking && (
        <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_auto]">
          <input className={field} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="De ce se redeschide (rămâne în istoric; semnătura veche nu mai e valabilă)" aria-label="Motivul redeschiderii" />
          <button type="button" className={quiet} disabled={busy || !reason.trim()} onClick={reopen}>Redeschide fișa</button>
        </div>
      )}
    </li>
  );
}

function Means({ data }) {
  if (!data.rows.length || !data.sheets.length) return null;
  return (
    <section aria-labelledby={`cp-means-${data.round}`}>
      <h2 id={`cp-means-${data.round}`} className="font-display text-xl font-bold text-[#373A45]">Punctajele și media</h2>
      <p className="mt-1 text-sm text-[#6B7280]">
        Media se calculează exact și se afișează cu două zecimale, fără rotunjire; {data.round === "final" ? "promovează programul" : "e admis"} cine are cel puțin {data.threshold} de
        puncte pe valoarea exactă (procedura, {data.round === "final" ? "art. 30 alin. 6" : "art. 28 alin. 4"}).
      </p>
      <div className={`mt-3 overflow-x-auto ${card}`}>
        <table className="w-full min-w-[30rem] text-sm">
          <thead>
            <tr className="border-b border-[#E5E7EB] text-left text-xs text-[#6B7280]">
              <th className="px-4 py-2.5 font-semibold">Cursant</th>
              {data.sheets.map((s, i) => <th key={s.trainer_id} className="px-3 py-2.5 text-right font-semibold" title={s.name}>Evaluator {i + 1}</th>)}
              <th className="px-3 py-2.5 text-right font-semibold">Media</th>
              <th className="px-4 py-2.5 font-semibold">Rezultat</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {data.rows.map((r) => (
              <tr key={r.enrollment_id}>
                <td className="px-4 py-2.5 font-semibold text-[#373A45]">{r.name}</td>
                {r.scores.map((s) => (
                  <td key={s.trainer_id} className="px-3 py-2.5 text-right tabular-nums text-[#373A45]">
                    {s.total_shown ?? "—"}
                    {data.round === "composition" && s.minutes !== null && s.minutes !== undefined && (
                      <span className={`block text-[10px] ${s.minutes < data.min_minutes ? "text-[#8a5d12]" : "text-[#6B7280]"}`}>{minutesLabel(s.minutes)}</span>
                    )}
                  </td>
                ))}
                <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{r.mean_shown ?? "—"}</td>
                <td className="px-4 py-2.5">
                  {r.verdict ? (
                    <Chip tone={r.verdict === "admis" ? "good" : "late"}>
                      {data.round === "final" ? (r.verdict === "admis" ? "Promovat" : "Nepromovat") : (r.verdict === "admis" ? "Admis" : "Respins")}
                    </Chip>
                  ) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!data.all_signed && <p className="mt-2 text-xs text-[#6B7280]">Media apare după ce semnează toți evaluatorii.</p>}
    </section>
  );
}

function Conditions({ data }) {
  if (!data.conditions.length) return null;
  return (
    <section aria-labelledby="cp-cond">
      <h2 id="cp-cond" className="font-display text-xl font-bold text-[#373A45]">Cine ajunge la susținere</h2>
      <p className="mt-1 text-sm text-[#6B7280]">
        Trei condiții, toate obligatorii (procedura, art. 30 alin. 2), fiecare citită din anexa ei: formarea încheiată (Anexa 5),
        admis la structură (Anexa 6), cel puțin 70 la compoziție (Anexa 9).
      </p>
      <div className={`mt-3 overflow-x-auto ${card}`}>
        <table className="w-full min-w-[30rem] text-sm">
          <thead>
            <tr className="border-b border-[#E5E7EB] text-left text-xs text-[#6B7280]">
              <th className="px-4 py-2.5 font-semibold">Cursant</th>
              <th className="px-3 py-2.5 font-semibold">Anexa 5</th>
              <th className="px-3 py-2.5 font-semibold">Anexa 6</th>
              <th className="px-3 py-2.5 font-semibold">Anexa 9</th>
              <th className="px-3 py-2.5 font-semibold">Prezent (10)</th>
              <th className="px-3 py-2.5 font-semibold">Promovat (12)</th>
              <th className="px-4 py-2.5 font-semibold">La susținere</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {data.conditions.map((r) => (
              <tr key={r.enrollment_id}>
                <td className="px-4 py-2.5 font-semibold text-[#373A45]">{r.name}</td>
                {[r.trained, r.structure, r.composition, r.present, r.final === "Absent" ? null : r.final].map((v, i) => {
                  const l = conditionLabel(v);
                  return <td key={i} className="px-3 py-2.5"><Chip tone={l.tone}>{l.label}</Chip></td>;
                })}
                <td className="px-4 py-2.5">{r.eligible ? <Chip tone="good">Da</Chip> : <span className="text-[#6B7280]">nu</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Annexes({ data, groupId, kind, round, onChanged }) {
  const L = ROUND_LABELS[round];
  const register = `annex_${L.register}`;
  const [registry, setRegistry] = useState({ no: "", date: "" });
  const [busy, setBusy] = useState("");
  const generate = async (what) => {
    setBusy(what);
    try {
      const r = await api.post(`/admin/portal/groups/${groupId}/composition-documents`, {
        session: kind, kind: what, registry_no: registry.no, registry_date: registry.date });
      toast.success(`Anexa ${L.register}, versiunea ${r.data.document.version}, e generată.`);
      onChanged(r.data);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut genera anexa."));
    } finally {
      setBusy("");
    }
  };
  const a9 = data.documents.filter((d) => d.kind === register);
  const a8 = data.documents.filter((d) => d.kind === `annex_${L.individual}`);
  return (
    <section aria-labelledby="cp-annex">
      <h2 id={`cp-annex-${round}`} className="font-display text-xl font-bold text-[#373A45]">Anexele {L.individual} și {L.register}</h2>
      <p className="mt-1 text-sm text-[#6B7280]">
        Anexa {L.individual} se generează singură când evaluatorul își semnează fișa (sau din rândul fișei, mai sus). Anexa {L.register} se generează
        după ce semnează toți. Generate,
        nu se mai schimbă: o corectură înseamnă o versiune nouă, iar cea veche rămâne.
      </p>
      <div className={`mt-3 grid gap-3 p-4 ${card}`} data-testid="cp-annex9">
        {!data.register.ready && (
          <ul className="list-disc pl-5 text-xs text-[#8a5d12]">{data.register.reasons.map((r) => <li key={r}>{r}</li>)}</ul>
        )}
        <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-center">
          <input className={field} value={registry.no} onChange={(e) => setRegistry({ ...registry, no: e.target.value })}
            placeholder="Nr. de înregistrare (opțional)" aria-label="Numărul din registru" />
          <input className={field} value={registry.date} onChange={(e) => setRegistry({ ...registry, date: e.target.value })}
            placeholder="din data (opțional)" aria-label="Data din registru" />
          <button type="button" className={quiet} disabled={!!busy || !data.register.ready} onClick={() => generate(register)}
            data-testid="cp-annex9-generate">
            {busy === register && <Loader2 size={13} className="animate-spin" />} Generează Anexa {L.register}
          </button>
        </div>
        {a9.map((d) => <Doc key={d.id} d={d} group={data.group.name} kind={kind} />)}
        {a8.length > 0 && <p className="mt-2 text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]">Anexa {L.individual}, pe evaluator</p>}
        {a8.map((d) => <Doc key={d.id} d={d} group={data.group.name} kind={kind} label={d.evaluator} />)}
      </div>
    </section>
  );
}

function Doc({ d, group, kind, label }) {
  const n = d.kind.replace("annex_", "");
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className={d.latest ? "font-semibold text-[#373A45]" : "text-[#6B7280]"}>
        {label ? `${label} · ` : ""}{versionLine(d)}{d.registry_no ? ` · nr. ${d.registry_no}` : ""}
      </span>
      {d.signed && <Chip tone="good">semnată</Chip>}
      {d.latest && d.stale && <Chip tone="warn">datele s-au schimbat</Chip>}
      {["pdf", "xlsx"].map((fmt) => (
        <button key={fmt} type="button" className="inline-flex items-center gap-1 font-semibold text-[#965EA7] hover:underline"
          onClick={() => downloadBlob(`/admin/portal/documents/${d.id}/${fmt}`,
            `Anexa_${n}_${asciiName(group)}_${kind}${label ? `_${asciiName(label)}` : ""}_v${d.version}.${fmt}`)}>
          <Download size={12} /> {fmt === "pdf" ? "PDF" : "Excel"}
        </button>
      ))}
    </div>
  );
}
